import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { FC } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { msalApp } from "../context/authContext";
import NotificationDispatch, { useNotification } from "../context/notificationContext";
import PermissionProvider, { usePermissionsContext } from "../context/permissionContext";
import useIdentity from "../hooks/useIdentity";
import useToggle from "../hooks/useToggle";
import useI18n from "../hooks/useTranslations";
import styles from "./App.module.css";
import { EditAdminClient, NewAdminClient } from "./clients/AdminClient";
import AdminClients from "./clients/AdminClients";
import OverviewReport from "./home/Overview";
import WeeklyReport from "./home/WeeklyReport";
import SvgHome from "./icons/Home";
import SvgPeople from "./icons/People";
import SvgViewTimeline from "./icons/ViewTimeline";
import Center from "./ui/Center";
import ErrorText from "./ui/ErrorText";
import LoadingSpinner from "./ui/LoadingSpinner";
import Nav from "./ui/Nav";
import Notification from "./ui/Notification";
import EditUser from "./users/EditUser";
import Users from "./users/Users";

const Spinner = () => (
  <Center>
    <LoadingSpinner />
  </Center>
);

const App: FC = () => {
  // const [isInitialized, setIsInitialized] = useState(false);

  // useEffect(() => {
  //   const initializeMsal = async () => {
  //     await msalApp.initialize(); // Ensure MSAL is fully initialized
  //     setIsInitialized(true);
  //   };

  //   initializeMsal().catch((err) => {
  //     console.error("MSAL initialization error:", err);
  //   });
  // }, []);

  // if (!isInitialized) {
  //   return <Spinner />; // Show a loading spinner until MSAL is initialized
  // }

  return (
    <MsalProvider instance={msalApp}>
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} loadingComponent={Spinner}>
        <BrowserRouter>
          <PermissionProvider>
            <AppRoutes />
          </PermissionProvider>
        </BrowserRouter>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};

export default App;

const AppRoutes: FC = () => {
  const i18n = useI18n();
  const [{ show, severity, message }, dispatch] = useNotification();
  const sidebar = useToggle();
  const identity = useIdentity();
  const permissionsContext = usePermissionsContext();

  return (
    <NotificationDispatch.Provider value={dispatch}>
      <nav className={styles.ribbon}>
        {/* <Hamburger onClick={sidebar.toggle} isOpen={sidebar.isOpen}></Hamburger> */}
        <div className={styles.title}>Jira Reports Rothoblaas</div>
        <div>
          {i18n.translation.common.hi} {identity.firstName}!
        </div>
      </nav>
      <div className={styles.wrapper}>
        {permissionsContext.hasAccess ? (
          <>
            <div className={styles.nav}>
              <Nav>
                <Nav.Group title={i18n.translation.common.reports}>
                  <Nav.Item text={i18n.translation.common.weeklyReport} to="/" icon={SvgHome} onClick={sidebar.close} />
                  <Nav.Item
                    text={i18n.translation.common.hoursPerProject}
                    to="/overview"
                    icon={SvgHome}
                    onClick={sidebar.close}
                  />
                </Nav.Group>
              </Nav>
              {permissionsContext.isAdmin && (
                <Nav>
                  <Nav.Group title={i18n.translation.common.administration}>
                    <Nav.Item
                      text={i18n.translation.users.plural}
                      to="admin/users"
                      icon={SvgPeople}
                      onClick={sidebar.close}
                    />
                    <Nav.Item
                      text={i18n.translation.clients.plural}
                      to="admin/clients"
                      icon={SvgViewTimeline}
                      onClick={sidebar.close}
                    />
                  </Nav.Group>
                </Nav>
              )}
            </div>
            <div className={styles.page}>
              <Routes>
                <Route index element={<WeeklyReport />} />
                <Route path="overview" element={<OverviewReport />} />

                {permissionsContext.isAdmin && (
                  <>
                    <Route path="admin/users" element={<Users />} />
                    <Route path="admin/users/:id" element={<EditUser />} />

                    <Route path="admin/clients" element={<AdminClients />} />
                    <Route path="admin/clients/new" element={<NewAdminClient />} />
                    <Route path="admin/clients/:id" element={<EditAdminClient />} />
                  </>
                )}
                <Route path="*" element={<ErrorText text={i18n.translation.common.pageNotFound} />} />
              </Routes>
            </div>
          </>
        ) : (
          <Center>
            {permissionsContext.isLoading ? (
              <LoadingSpinner />
            ) : permissionsContext.error ? (
              <ErrorText text={i18n.translation.common.genericError} />
            ) : (
              i18n.translation.common.youAreNotAllowed
            )}
          </Center>
        )}
      </div>
      {show && <Notification type={severity} text={message ?? ""} />}
    </NotificationDispatch.Provider>
  );
};
